import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { ValidationError } from "joi";
// Material UI
import { Box, SvgIcon } from "@mui/material";
import { LoadingButton } from "@mui/lab";

// Third Party
import clsx from "clsx";

// Components
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import { TSelectValue } from "@/Layouts/components/InputComponents/SmartSelector/interfaces";
import SimpleDoubleChecking from "@/Layouts/components/Buttons/SimpleDoubleChecking";

// Helpers
import { and, or } from "@/Common/helpers/sonar";

// Icons
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { ReactComponent as TrashIcon } from "@/assets/icons/dataPages/inventory/transfer-trash.svg";

// Types
import { TSearchTransferRequest, TTransferInOut } from "../../types";
import { TFilterOptionsArgs } from "../../types/dealerTrades";

// Constants
import { STATUS_OPTIONS } from "../../constants";

// Helpers
import {
	arraysEqualIgnoreOrder,
	checkAppendInMultiSelect,
	filterAndGroupInventoryOptions,
	findError,
	formatSelected,
	getDisabledOptions,
	getOptions,
	getStores,
	isTransferIn,
	keepLastDestination,
	resetFields,
	resolveDisableStockVINandMake,
	stringArraysEqualIgnoreOrder,
} from "../../helpers";
import { getInventoryFilterOptions } from "@/DataPages/api/inventory/queries";
import { transferRequestFilterSchema } from "./validation";
import { groupFranchisesByOems } from "@/Common/helpers/locations";
import { useInventoryContext } from "../../contexts/InventoryFilterContext";

type TTransferRequestSearchProps = {
	locationId: string;
	transferType: TTransferInOut;
	searchTransferRequest?: Partial<TSearchTransferRequest>;
	onChangeTransferRequest: (data: Partial<TSearchTransferRequest>) => void;
	clearSearchTransferRequest: () => void;
	searchTransferRequestHandler: () => void;
	locationsOptions: {
		isLoadingLocations: boolean;
		mappedLocations: { value: string; label: string }[];
		franchisesByLocations: { franchises: string[]; locationId: string }[];
	};
	defaultSearchTransferRequest?: Partial<TSearchTransferRequest>;
	disableLocationOptionsAndValidation?: boolean;
	table: string;
	isCurrentPayPeriod: boolean;
};

export const TransferRequestSearch = (props: Readonly<TTransferRequestSearchProps>) => {
	const {
		transferType,
		locationId,
		searchTransferRequest = {} as unknown as TSearchTransferRequest,
		locationsOptions,
		onChangeTransferRequest,
		clearSearchTransferRequest,
		searchTransferRequestHandler,
		defaultSearchTransferRequest,
		disableLocationOptionsAndValidation,
		table,
		isCurrentPayPeriod,
	} = props;
	const storeLocation = useSelector((state: RootState) => state.location.location);
	const storeFranchises = groupFranchisesByOems(storeLocation?.oems ?? []);
	const [errors, setErrors] = useState<ValidationError | null>(null);

	const { clearFilterSelection } = useInventoryContext();

	const getLocationIdsFromGroup = (group: string) =>
		searchTransferRequest.location
			?.filter((l: { value: string; group: string }) => l.group === group)
			.map((loc) => loc.value)
			.sort() ?? [];

	const originLocations = getLocationIdsFromGroup("origin");
	const getOptionsParams: TFilterOptionsArgs = {
		status: searchTransferRequest.status ?? [],
		mappedLocations: searchTransferRequest.location ?? [],
		franchisesByLocations: locationsOptions.franchisesByLocations,
	};
	const destinationLocation = getLocationIdsFromGroup("destination")[0];

	const { data: inventoryFilterOptions, isLoading: isLoadingInventoryFilterOptions } = useQuery({
		queryKey: ["getInventoryFilterOptions", locationId, searchTransferRequest.status?.sort(), originLocations],
		queryFn: async () => getInventoryFilterOptions(locationId, getOptionsParams),
		enabled:
			!!locationId &&
			(searchTransferRequest.status?.length ?? 0) > 0 &&
			(originLocations?.length ?? 0) > 0 &&
			(destinationLocation?.length ?? 0) > 0,
		staleTime: Infinity,
	});

	const inventoryOptions = filterAndGroupInventoryOptions({
		inventoryFilterOptions,
		searchTransferRequest,
		destinationStoreFranchises:
			locationsOptions.franchisesByLocations.find((item) => item.locationId === destinationLocation)?.franchises ?? [],
		statusSelected: searchTransferRequest.status ?? [],
	});

	const updateSearchTransferRequest = (key: keyof TSearchTransferRequest, value: TSelectValue | string | string[]) => {
		const isAppend = checkAppendInMultiSelect({ key, value, searchTransferRequest });
		onChangeTransferRequest({
			transferType,
			...resetFields({
				key,
				transferType,
				locationsOptions,
				locationId,
				defaultSearchTransferRequest,
				disableLocationOptionsAndValidation,
				isAppend,
				value: searchTransferRequest,
			}),
			[key]: value,
		});
	};

	const handleMultipleChange = (key: keyof TSearchTransferRequest, selectedItems: TSelectValue) => {
		if (!selectedItems || !Array.isArray(selectedItems)) {
			return;
		}
		const callBack = () => {
			const foundKey = searchTransferRequest[key] as string[];
			const mappedSelectedItems = selectedItems?.map((item) => item.value ?? item.label) ?? [];
			if (stringArraysEqualIgnoreOrder(mappedSelectedItems, foundKey)) {
				return;
			}
			updateSearchTransferRequest(key, mappedSelectedItems);
		};
		const transferRequestMap: Partial<Record<keyof TSearchTransferRequest, Function>> = {
			status: callBack,
			make: callBack,
			model: callBack,
			trim: callBack,
			year: callBack,
			stockNumber: callBack,
			vinNumber: callBack,
			location: () => {
				const foundKey = searchTransferRequest[key] as { value: string; group: string }[];
				const mappedSelectedItems = selectedItems?.map((item) => ({ value: item.value, group: item.group })) ?? [];
				if (arraysEqualIgnoreOrder(mappedSelectedItems, foundKey)) {
					return;
				}
				if (!isTransferIn(transferType)) {
					const filteredSelectedItems = keepLastDestination(mappedSelectedItems);
					updateSearchTransferRequest(key, filteredSelectedItems);
				} else {
					updateSearchTransferRequest(key, mappedSelectedItems);
				}
			},
		};

		const action = transferRequestMap[key];

		if (action) {
			action();
		}
	};

	const disableStockVINandMake = resolveDisableStockVINandMake(transferType, searchTransferRequest);

	const handleOnDeleteAction = () => {
		clearFilterSelection([{ table }]);
		clearSearchTransferRequest();
	};

	useEffect(() => {
		if (defaultSearchTransferRequest) {
			const updatedValue = Object.entries(defaultSearchTransferRequest).reduce<Partial<TSearchTransferRequest>>(
				(acc, [key, value]) => {
					if (Array.isArray(value) && value.length) {
						return { ...acc, [key]: value };
					}
					if (typeof value === "string" && value) {
						return { ...acc, [key]: value };
					}
					return acc;
				},
				{}
			);
			return onChangeTransferRequest({ transferType, ...updatedValue });
		}
		if (isTransferIn(transferType)) {
			const newValue = [{ ...locationsOptions.mappedLocations.find((loc) => loc.value === locationId), group: "destination" }];
			return handleMultipleChange("location", newValue);
		} else {
			const newValue = [{ ...locationsOptions.mappedLocations.find((loc) => loc.value === locationId), group: "origin" }];
			return handleMultipleChange("location", newValue);
		}
	}, []);

	useEffect(() => {
		const validation = transferRequestFilterSchema.validate(
			{ ...searchTransferRequest, make: searchTransferRequest.make ?? [], model: searchTransferRequest.model ?? [] },
			{
				abortEarly: false,
				allowUnknown: true,
			}
		);
		setErrors(validation?.error ?? null);
		return () => {
			setErrors(null);
		};
	}, [searchTransferRequest]);

	const mapNumbers = (items: string[] = []) => items.map((value: string) => ({ label: value }));

	return (
		<Box className="flex flex-row items-start justify-between gap-1">
			<Box>
				<SmartSelector
					multiple
					tagsVariant="text"
					classes="!w-[130px]"
					showBy={"label"}
					selectBy="value"
					orderBy="label"
					options={STATUS_OPTIONS}
					selected={formatSelected(searchTransferRequest?.status)}
					loading={isLoadingInventoryFilterOptions}
					inputProps={{
						placeholder: "New/Used",
						hideTooltip: true,
					}}
					popperProps={{ className: "!z-[1350]" } as object}
					hideTooltip={true}
					onChangeAction={(value) => handleMultipleChange("status", value)}
					error={!!findError(errors, ["status"])}
					disabled={!isCurrentPayPeriod}
				/>
			</Box>
			<Box>
				<SmartSelector
					popperProps={{ className: "!z-[1350]" } as object}
					classes="!w-[230px] [&_.MuiChip-root]:!font-sans"
					tagsVariant="text"
					showBy="label"
					selectBy="value"
					orderBy="group"
					disabled={or(!searchTransferRequest.status?.length, !isCurrentPayPeriod)}
					options={getOptions(transferType, locationId, locationsOptions.mappedLocations)}
					disabledOptions={getDisabledOptions({
						locationId,
						storeFranchises,
						disableLocationOptions: disableLocationOptionsAndValidation,
						franchisesByLocations: locationsOptions.franchisesByLocations,
						locations: locationsOptions.mappedLocations,
						status: searchTransferRequest.status ?? [],
					})}
					groupingProps={{
						groupBy: "group",
						defaultLabel: "Unknown",
						sticky: true,
						specificProperties: {
							origin: {
								classes: "!text-primary !text-lg",
								sx: { fontVariant: "small-caps !important" },
							},
							destination: {
								classes: "!text-neutral-gray !text-lg",
								sx: { fontVariant: "small-caps !important" },
							},
						},
					}}
					selected={
						getStores({
							locationsSelected: searchTransferRequest?.location,
							locationsOptions,
						}) ?? []
					}
					multiple
					loading={isLoadingInventoryFilterOptions}
					inputProps={{
						placeholder: "Store(s)",
					}}
					onChangeAction={(value) => {
						if ((value as object[])?.length) {
							handleMultipleChange("location", value);
						}
					}}
					error={!!findError(errors, ["location"])}
				/>
			</Box>
			<Box>
				<SmartSelector
					addable
					multiple
					showBy={"label"}
					selectBy={"label"}
					options={mapNumbers(searchTransferRequest.stockNumber)}
					selected={mapNumbers(searchTransferRequest.stockNumber)}
					inputProps={{
						placeholder: "Stock Number(s)",
						classes: `[&_.MuiInputBase-root]:!py-[3px]`,
					}}
					renderTagsCustomProps={{
						classes: "!font-sans",
					}}
					onChangeAction={(value) => handleMultipleChange("stockNumber", value)}
					loading={isLoadingInventoryFilterOptions}
					disabled={or(disableStockVINandMake, !isCurrentPayPeriod)}
					clearable={Boolean(mapNumbers(searchTransferRequest.stockNumber)?.length)}
					classes="!w-[130px]"
					popperProps={{ className: "!z-[1350]" } as object}
				/>
			</Box>
			<Box>
				<SmartSelector
					addable
					multiple
					showBy={"label"}
					selectBy={"label"}
					options={mapNumbers(searchTransferRequest.vinNumber)}
					selected={mapNumbers(searchTransferRequest.vinNumber)}
					inputProps={{
						placeholder: "VIN Number(s)",
						classes: `[&_.MuiInputBase-root]:!py-[3px]`,
					}}
					renderTagsCustomProps={{
						classes: "!font-sans",
					}}
					onChangeAction={(value) => handleMultipleChange("vinNumber", value)}
					loading={isLoadingInventoryFilterOptions}
					disabled={or(disableStockVINandMake, !isCurrentPayPeriod)}
					clearable={Boolean(mapNumbers(searchTransferRequest.vinNumber)?.length)}
					classes="!w-[130px]"
					popperProps={{ className: "!z-[1350]" } as object}
				/>
			</Box>

			<Box>
				<SmartSelector
					popperProps={{ className: "!z-[1350]" } as object}
					clearable
					multiple
					tagsVariant="text"
					classes="!w-[130px]"
					showBy={"label"}
					selectBy="value"
					orderBy="label"
					options={inventoryOptions.make}
					selected={formatSelected(searchTransferRequest.make)}
					error={!!findError(errors, ["make"])}
					disabled={or(disableStockVINandMake, !isCurrentPayPeriod)}
					loading={isLoadingInventoryFilterOptions}
					inputProps={{
						placeholder: "Make",
					}}
					onChangeAction={(value) => handleMultipleChange("make", value)}
				/>
			</Box>
			<Box>
				<SmartSelector
					popperProps={{ className: "!z-[1350]" } as object}
					clearable
					multiple
					tagsVariant="text"
					classes="!w-[130px]"
					showBy={"label"}
					selectBy="value"
					orderBy="label"
					options={inventoryOptions.model}
					selected={formatSelected(searchTransferRequest.model)}
					error={!!findError(errors, ["model"])}
					disabled={or(disableStockVINandMake, !searchTransferRequest.make?.length, !isCurrentPayPeriod)}
					loading={isLoadingInventoryFilterOptions}
					inputProps={{
						placeholder: "Model",
					}}
					onChangeAction={(value) => handleMultipleChange("model", value)}
				/>
			</Box>
			<Box>
				<SmartSelector
					popperProps={{ className: "!z-[1350]" } as object}
					clearable
					multiple
					tagsVariant="text"
					classes="!w-[130px]"
					showBy={"label"}
					selectBy="value"
					orderBy="label"
					options={inventoryOptions.trim}
					selected={formatSelected(searchTransferRequest.trim)}
					disabled={or(disableStockVINandMake, !searchTransferRequest.model?.length, !isCurrentPayPeriod)}
					loading={isLoadingInventoryFilterOptions}
					inputProps={{
						placeholder: "Trim",
					}}
					onChangeAction={(value) => handleMultipleChange("trim", value)}
				/>
			</Box>
			<Box>
				<SmartSelector
					popperProps={{ className: "!z-[1350]" } as object}
					multiple
					tagsVariant="text"
					classes="!w-[130px]"
					showBy={"label"}
					selectBy="value"
					orderBy="label"
					clearable
					options={inventoryOptions.year}
					selected={formatSelected(searchTransferRequest.year)}
					loading={isLoadingInventoryFilterOptions}
					disabled={or(disableStockVINandMake, !searchTransferRequest.trim?.length, !isCurrentPayPeriod)}
					inputProps={{
						placeholder: "Year",
					}}
					onChangeAction={(value) => handleMultipleChange("year", value)}
				/>
			</Box>
			<LoadingButton
				variant="contained"
				classes={{
					loadingIndicator: "!text-white",
					root: clsx({
						"h-[30px] !w-[80px] !rounded-md !capitalize !font-sans !text-white": true,
						"!bg-primary": and(!isLoadingInventoryFilterOptions, !errors, isCurrentPayPeriod),
						"!bg-disabled": or(isLoadingInventoryFilterOptions, !!errors, !isCurrentPayPeriod),
					}),
				}}
				disabled={or(isLoadingInventoryFilterOptions, !!errors)}
				onClick={searchTransferRequestHandler}
				disableElevation
			>
				Search
			</LoadingButton>
			<SimpleDoubleChecking
				color={"var(--gray-blue-color)"}
				confirmationButtonColor={"var(--gray-blue-color)"}
				icon={<CloseRoundedIcon fontSize="medium" sx={{ width: "30px", height: "30px" }} />}
				iconHover={<CloseRoundedIcon fontSize="medium" sx={{ width: "30px", height: "30px" }} />}
				confirmationIcon={<SvgIcon component={TrashIcon} sx={{ width: "26px", height: "26px", paddingLeft: "3px" }} />}
				confirmationSx={{ padding: "10px" }}
				onDeleteAction={handleOnDeleteAction}
				disabled={!isCurrentPayPeriod}
				size="medium"
				sx={{ height: "30px", marginLeft: "-5px", marginTop: "-8px" }}
			/>
		</Box>
	);
};
